import { ref, computed, watch, onUnmounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Auth from 'dashboard/api/auth';
import wootConstants from 'dashboard/constants/globals';
import { useAgentPermissionOptions } from './useAgentPermissionOptions';
import {
  AgentAssignmentError,
  agentAssignmentPayload,
  agentAssignmentValue,
} from './agentAssignment';

export function useAgentEditor(props, close) {
  const store = useStore();
  const { t } = useI18n();
  const options = useAgentPermissionOptions();
  const previous = {
    role: props.type,
    permission_set_id: props.permissionSetId,
    custom_role_id: props.customRoleId,
  };
  const initial = agentAssignmentValue(previous);
  const selectedRoleId = ref(initial);
  const agentName = ref(props.name);
  const agentAvailability = ref(props.availability);
  const v$ = useVuelidate(
    {
      agentName: { required },
      selectedRoleId: { required },
      agentAvailability: { required },
    },
    { agentName, selectedRoleId, agentAvailability }
  );
  const uiFlags = useMapGetter('agents/getUIFlags');
  const pageTitle = computed(
    () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
  );
  let active = true;
  const invalidate = () => {
    active = false;
  };
  watch(options.scope, () => {
    invalidate();
    close();
  });
  onUnmounted(invalidate);

  const assignment = selection =>
    agentAssignmentPayload({
      selection,
      previous,
      ownEnabled: options.ownEnabled.value,
      nativeEnabled: options.nativeEnabled.value,
      enterprise: options.enterprise,
    });
  const roles = computed(() => {
    const available = [...options.roles.value];
    if (!available.some(role => role.id === initial)) {
      available.push({
        id: initial,
        label:
          previous.permission_set_id != null
            ? t('PERMISSION_SETS.ASSIGNMENT.CURRENT_OWN')
            : t('PERMISSION_SETS.ASSIGNMENT.CURRENT_NATIVE'),
      });
    }
    return available.map(role => {
      try {
        assignment(role.id);
        return role;
      } catch (error) {
        if (!(error instanceof AgentAssignmentError)) throw error;
        return { ...role, disabled: true };
      }
    });
  });
  const canSubmit = computed(
    () =>
      !v$.value.$invalid &&
      !options.loading.value &&
      !options.error.value &&
      !uiFlags.value.isUpdating &&
      roles.value.some(
        role => role.id === selectedRoleId.value && !role.disabled
      )
  );
  const availabilityStatuses = computed(() =>
    [
      t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
      t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
      t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
    ].map((label, index) => ({
      label,
      value: wootConstants.AVAILABILITY_STATUS_KEYS[index],
    }))
  );

  const editAgent = async () => {
    v$.value.$touch();
    if (!active || !canSubmit.value) return;
    try {
      const payload = assignment(selectedRoleId.value);
      Object.assign(payload.agent, {
        name: agentName.value,
        availability: agentAvailability.value,
      });
      const result = await store.dispatch('agents/update', {
        id: props.id,
        ...payload,
      });
      if (!active || result === null) return;
      useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      close();
    } catch {
      if (active) useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
    }
  };
  const resetPassword = async () => {
    try {
      await Auth.resetPassword({ email: props.email });
      if (active)
        useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
    } catch {
      if (active) useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
    }
  };
  return {
    agentName,
    agentAvailability,
    selectedRoleId,
    v$,
    uiFlags,
    pageTitle,
    roles,
    availabilityStatuses,
    editAgent,
    resetPassword,
    canSubmit,
    rolesLoading: options.loading,
    rolesError: options.error,
    load: options.load,
  };
}
